import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  TextField,
  Autocomplete,
  Chip,
  Drawer,
  CircularProgress,
  Stack,
} from "@mui/material";
import get from "lodash.get";

import { useRequest, REQUEST_STATUS } from "hooks";
import { SearchParams } from "services/urlParams/urlParams";
import {
  PageWrapper,
  FormCard,
  SectionTitle,
  SectionDivider,
} from "./OrganizationReset.style";
import { SaveButton, Table } from "components";
import { FilterSelect } from "components/common/FilterSelect/FilterSelect";
import { dateCovert } from "services/dateConvert/dateConvert";

export interface ScopeOption {
  value: string;
  description: string;
}

/** organization/pagin response: { total, data } */
export interface OrganizationOption {
  _id: string;
  id?: string;
  name: string;
  username?: string;
  subdomain?: string;
  [key: string]: unknown;
}

export interface BranchOption {
  _id: string;
  name: string;
}

/** organization-reset/academic-years response item */
export interface AcademicYearOption {
  _id: string;
  organizationId: string;
  startsAt: string;
  endsAt: string;
  isCurrent?: boolean;
  [key: string]: unknown;
}

/** organization-reset/history response item */
export interface ResetHistoryItem {
  _id: string;
  organizationId?: string;
  createdAt?: string;
  performedAt?: string;
  branchIds?: string[];
  resetTypes?: string[];
  academicYearIds?: string[];
  organization?: { _id: string; name: string };
  branches?: { _id: string; name: string }[];
  academicYears?: { _id: string; startsAt?: string; endsAt?: string }[];
  [key: string]: unknown;
}

const RESET_TYPES: ScopeOption[] = [
  "allFinance",
  "studentBalances",
  "employeeBalances",
  "transactions",
  "cashboxBalances",
  "finmap",
  "integrationTransactions",
  "callHistory",
  "studentContracts",
  "attendances",
  "leads",
  "students",
  "studentSubscriptions",
  "lessonSchedules",
].map((value) => ({ value, description: "" }));

const OrganizationReset = () => {
  const { t } = useTranslation();
  const [client, , resetStatus] = useRequest();
  const [, getParams] = SearchParams();
  const [filter, setFilter] = useState(getParams());

  const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
  const [organizationId, setOrganizationId] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [organizationInputValue, setOrganizationInputValue] = useState("");

  const [selectedScopes, setSelectedScopes] = useState<ScopeOption[]>([]);

  const [branches, setBranches] = useState<BranchOption[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState<BranchOption[]>([]);

  const [academicYears, setAcademicYears] = useState<AcademicYearOption[]>([]);
  const [academicYearsLoading, setAcademicYearsLoading] = useState(false);
  const [selectedAcademicYear, setSelectedAcademicYear] =
    useState<AcademicYearOption | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [render, setRender] = useState(false);

  // organization/pagin qaytaradi: { total, data: OrganizationOption[] }
  useEffect(() => {
    client
      .post("organization/pagin", { limit: 200, page: 1 })
      .then((res: any) => {
        const data = get(res, "data.data", []) as OrganizationOption[];
        if (Array.isArray(data)) setOrganizations(data);
      })
      .catch((e: any) => console.error("Failed to load organizations", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Filiallar organizationId o‘zgaganda
  useEffect(() => {
    if (!organizationId.trim()) {
      setBranches([]);
      setSelectedBranches([]);
      return;
    }
    let cancelled = false;
    setBranchesLoading(true);
    client
      .get(
        `organization-reset/branches?organizationId=${encodeURIComponent(organizationId)}`
      )
      .then((res: any) => {
        if (cancelled) return;
        const data = get(res, "data", []);
        if (Array.isArray(data)) setBranches(data);
        else setBranches([]);
      })
      .catch((e: any) => {
        if (!cancelled) console.error("Failed to load branches", e);
        setBranches([]);
      })
      .finally(() => {
        if (!cancelled) setBranchesLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  // O‘quv yillari organizationId o‘zgaganda
  useEffect(() => {
    if (!organizationId.trim()) {
      setAcademicYears([]);
      setSelectedAcademicYear(null);
      return;
    }
    let cancelled = false;
    setAcademicYearsLoading(true);
    client
      .get(
        `organization-reset/academic-years?organizationId=${encodeURIComponent(organizationId)}`
      )
      .then((res: any) => {
        if (cancelled) return;
        const data = get(res, "data", []) as AcademicYearOption[];
        if (Array.isArray(data)) setAcademicYears(data);
        else setAcademicYears([]);
      })
      .catch((e: any) => {
        if (!cancelled) console.error("Failed to load academic years", e);
        setAcademicYears([]);
      })
      .finally(() => {
        if (!cancelled) setAcademicYearsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const formatAcademicYearLabel = (opt: AcademicYearOption) => {
    const start = opt.startsAt ? new Date(opt.startsAt).getFullYear() : "";
    const end = opt.endsAt ? new Date(opt.endsAt).getFullYear() : "";
    const current = opt.isCurrent ? ` (${t("ORGANIZATION_RESET.ACADEMIC_YEAR_CURRENT")})` : "";
    return `${start}${end ? ` — ${end}` : ""}${current}`;
  };

  const handleSubmit = async () => {
    if (!organizationId.trim() || !selectedAcademicYear) return;
    const resetTypes = selectedScopes.map((s) => s.value);
    if (resetTypes.length === 0) return;

    try {
      await client.post("organization-reset/execute", {
        organizationId,
        branchIds: selectedBranches.map((b) => b._id),
        academicYearIds: selectedAcademicYear ? [selectedAcademicYear._id] : [],
        resetTypes,
      });
      setDrawerOpen(false);
      setOrganizationId("");
      setOrganizationName("");
      setOrganizationInputValue("");
      setSelectedAcademicYear(null);
      setSelectedBranches([]);
      setSelectedScopes([]);
      setRender((r) => !r);
    } catch (_) {
      // error shown by useRequest toast
    }
  };

  const isSubmitDisabled =
    !organizationName.trim() ||
    !selectedAcademicYear ||
    selectedScopes.length === 0 ||
    selectedBranches.length === 0 ||
    resetStatus === REQUEST_STATUS.loading;

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      fontSize: "14px",
      "& fieldset": { borderColor: "#edf1ff" },
      "&:hover fieldset": { borderColor: "#3d68ff", borderWidth: "1px" },
      "&.Mui-focused fieldset": { borderWidth: "1px", borderColor: "#3d68ff" },
    },
    "& .MuiInputLabel-root": { fontSize: "14px" },
  };

  const formatAcademicYearRange = (ay: { startsAt?: string; endsAt?: string }) => {
    const start = ay.startsAt ? new Date(ay.startsAt).getFullYear() : "";
    const end = ay.endsAt ? new Date(ay.endsAt).getFullYear() : "";
    return start && end ? `${start} — ${end}` : start || end || "—";
  };

  const historyColumns = [
    {
      title: t("ORGANIZATION_RESET.HISTORY_COL_DATE"),
      renderItem: (item: ResetHistoryItem) => (
        <Box component="span" sx={{ whiteSpace: "nowrap", fontSize: "13px" }}>
          {item.createdAt ? dateCovert(item.createdAt) : ""}
        </Box>
      ),
    },
    {
      title: t("ORGANIZATION_RESET.HISTORY_COL_ORGANIZATION"),
      renderItem: (item: ResetHistoryItem) => (
        <Box sx={{ fontWeight: 500, fontSize: "14px" }}>
          {item.organization?.name ?? item.organizationId ?? "—"}
        </Box>
      ),
    },
    {
      title: t("ORGANIZATION_RESET.HISTORY_COL_BRANCHES"),
      renderItem: (item: ResetHistoryItem) =>
        Array.isArray(item.branches) && item.branches.length > 0 ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {item.branches.map((b) => (
              <Chip
                key={b._id}
                label={b.name}
                size="small"
                variant="outlined"
                sx={{ borderRadius: "6px", fontSize: "12px", borderColor: "divider" }}
              />
            ))}
          </Box>
        ) : (
          <Box component="span" sx={{ color: "text.secondary", fontSize: "13px" }}>—</Box>
        ),
    },
    {
      title: t("ORGANIZATION_RESET.HISTORY_COL_ACADEMIC_YEARS"),
      renderItem: (item: ResetHistoryItem) =>
        Array.isArray(item.academicYears) && item.academicYears.length > 0 ? (
          <Box sx={{ fontSize: "13px" }}>
            {item.academicYears.map((ay, i) => (
              <Box key={ay._id || i} component="span">
                {formatAcademicYearRange(ay)}
                {i < item.academicYears!.length - 1 ? ", " : ""}
              </Box>
            ))}
          </Box>
        ) : (
          <Box component="span" sx={{ color: "text.secondary", fontSize: "13px" }}>—</Box>
        ),
    },
    {
      title: t("ORGANIZATION_RESET.HISTORY_COL_SCOPES"),
      renderItem: (item: ResetHistoryItem) =>
        Array.isArray(item.resetTypes) && item.resetTypes.length > 0 ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {item.resetTypes.slice(0, 6).map((s) => (
              <Chip
                key={s}
                label={t(`ORGANIZATION_RESET_SCOPES.${s}`, { defaultValue: s })}
                size="small"
                sx={{
                  borderRadius: "6px",
                  fontSize: "11px",
                  bgcolor: "action.hover",
                  fontWeight: 500,
                }}
              />
            ))}
            {item.resetTypes.length > 6 && (
              <Chip
                label={`+${item.resetTypes.length - 6}`}
                size="small"
                sx={{ borderRadius: "6px", fontSize: "11px" }}
              />
            )}
          </Box>
        ) : (
          <Box component="span" sx={{ color: "text.secondary", fontSize: "13px" }}>—</Box>
        ),
    },
  ];

  return (
    <div className="container-scroll">
      <PageWrapper>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <FilterSelect
              label={t("ORGANIZATION_RESET.SELECT_ORGANIZATION")}
              value={filter.organizationId}
              setFilter={setFilter}
              name="organizationId"
              options={[
                ...organizations.map((o) => ({ value: o._id, name: o.name || o.username || "" })),
              ]}
            />
          </Stack>
          <SaveButton
            value={`${t("ORGANIZATION_RESET.BUTTON_RESET")}`}
            onClick={() => setDrawerOpen(true)}
            type="button"
          />
        </Box>

        <Table
          dataUrl="organization-reset/history"
          columns={historyColumns}
          filter={filter}
          render={render}
        />
      </PageWrapper>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: { xs: "100%", sm: 440 }, maxWidth: "100%", p: 2 },
        }}
      >
        <FormCard elevation={0}>
          <SectionTitle>{t("ORGANIZATION_RESET.DRAWER_TITLE")}</SectionTitle>
          <SectionDivider />
          <SectionTitle>{t("ORGANIZATION_RESET.SELECT_ORGANIZATION")}</SectionTitle>
          <Autocomplete
            fullWidth
            options={organizations}
            getOptionLabel={(opt) => opt.name || opt.username || ""}
            value={
              organizations.find((o) => o._id === organizationId) ||
              organizations.find((o) => o.username === organizationName) ||
              null
            }
            onChange={(_, newValue) => {
              const id = newValue?._id ?? "";
              const userName = newValue?.username ?? "";
              setOrganizationId(id);
              setOrganizationName(userName);
            }}
            inputValue={organizationInputValue}
            onInputChange={(_, value) => {
              setOrganizationInputValue(value);
              const body: { limit: number; page: number; search?: string } = { limit: 200, page: 1 };
              if (value && value.trim()) body.search = value.trim();
              client
                .post("organization/pagin", body)
                .then((res: any) => {
                  const data = get(res, "data.data", []) as OrganizationOption[];
                  if (Array.isArray(data)) setOrganizations(data);
                })
                .catch(() => { });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("ORGANIZATION_RESET.ORGANIZATION_NAME")}
                placeholder={t("ORGANIZATION_RESET.ORGANIZATION_NAME_PLACEHOLDER")}
                size="small"
                sx={inputSx}
              />
            )}
          />

          <SectionDivider />
          <SectionTitle>{t("ORGANIZATION_RESET.SELECT_ACADEMIC_YEAR")}</SectionTitle>
          <Autocomplete<AcademicYearOption>
            fullWidth
            options={academicYears}
            getOptionLabel={formatAcademicYearLabel}
            value={selectedAcademicYear}
            onChange={(_, newValue) => setSelectedAcademicYear(newValue)}
            loading={academicYearsLoading}
            disabled={!organizationId.trim()}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("ORGANIZATION_RESET.ACADEMIC_YEAR")}
                placeholder={
                  organizationId
                    ? t("ORGANIZATION_RESET.ACADEMIC_YEAR_PLACEHOLDER")
                    : t("ORGANIZATION_RESET.SELECT_ORG_FIRST")
                }
                size="small"
                sx={inputSx}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {academicYearsLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <SectionDivider />
          <SectionTitle>{t("ORGANIZATION_RESET.SELECT_BRANCHES")}</SectionTitle>
          <Autocomplete
            multiple
            fullWidth
            options={branches}
            getOptionLabel={(opt) => opt.name || ""}
            value={selectedBranches}
            onChange={(_, newValue) => setSelectedBranches(newValue)}
            loading={branchesLoading}
            disabled={!organizationId.trim()}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("ORGANIZATION_RESET.BRANCHES")}
                placeholder={
                  organizationId
                    ? t("ORGANIZATION_RESET.BRANCHES_PLACEHOLDER")
                    : t("ORGANIZATION_RESET.SELECT_ORG_FIRST")
                }
                size="small"
                sx={inputSx}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {branchesLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.name}
                  size="small"
                  sx={{ borderRadius: "8px", fontSize: "13px" }}
                  {...getTagProps({ index })}
                />
              ))
            }
          />

          <SectionDivider />
          <SectionTitle>{t("ORGANIZATION_RESET.SELECT_SCOPES")}</SectionTitle>
          <Autocomplete
            multiple
            fullWidth
            options={RESET_TYPES}
            getOptionLabel={(opt) =>
              `${t(`ORGANIZATION_RESET_SCOPES.${opt.value}`, { defaultValue: opt.value })} — ${t(
                `ORGANIZATION_RESET_SCOPES.${opt.value}_DESC`,
                { defaultValue: opt.description }
              )}`
            }
            value={selectedScopes}
            onChange={(_, newValue) => setSelectedScopes(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("ORGANIZATION_RESET.SCOPES")}
                placeholder={t("ORGANIZATION_RESET.SCOPES_PLACEHOLDER")}
                size="small"
                sx={inputSx}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                <Box>
                  <strong>
                    {t(`ORGANIZATION_RESET_SCOPES.${option.value}`, {
                      defaultValue: option.value,
                    })}
                  </strong>
                  <br />
                  <small style={{ color: "#666" }}>
                    {t(`ORGANIZATION_RESET_SCOPES.${option.value}_DESC`, {
                      defaultValue: option.description,
                    })}
                  </small>
                </Box>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={t(`ORGANIZATION_RESET_SCOPES.${option.value}`, { defaultValue: option.value })}
                  size="small"
                  sx={{ borderRadius: "8px", fontSize: "13px" }}
                  {...getTagProps({ index })}
                />
              ))
            }
          />

          <Box sx={{ mt: 2.5, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <SaveButton
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              status={resetStatus}
              value={t("ORGANIZATION_RESET.BUTTON_RESET")}
            />
          </Box>
        </FormCard>
      </Drawer>
    </div>
  );
};

export default OrganizationReset;
